import { forwardRef, Ref } from 'preact/compat';
import { useCallback, useState } from 'preact/hooks';

import cx from 'clsx';

import {
	IInputProps,
	InputBaseType,
} from '@shared/components/core/input/Input.types';
import { Image } from '@shared/components/core';

import InfoRed from '@assets/icons/infoExclamationRed.svg';

import styles from './Input.module.scss';

const InputBase = forwardRef<InputBaseType, IInputProps>(
	(
		{
			type = 'text',
			value = '',
			label = '',
			placeholder = '',
			onChange,
			className,
			prefix,
			suffix,
			onBlur,
			errorMessage,
			showError = false,
			touched = false,
			wrapperClassName = '',
			onPaste,
			disabled = false,
			labelClassName = '',
			required = false,
			multiline = false,
			...props
		},
		ref,
	) => {
		const [hasBlurred, setHasBlurred] = useState(false);

		const handleBlur = useCallback(
			(event: Event) => {
				const target = event.target as HTMLInputElement;
				// Only mark as blurred if field has value (is dirty)
				if (target.value) {
					setHasBlurred(true);
				}
				if (onBlur) {
					onBlur(event);
				}
			},
			[onBlur],
		);

		const handleChange = useCallback(
			(event: Event) => {
				const target = event.target as HTMLInputElement;
				if (onChange) {
					onChange(target.value, event);
				}
			},
			[onChange],
		);

		// Show error when:
		// 1. showError is true and errorMessage exists
		// 2. Field has been blurred at least once
		const shouldShowError =
			showError && errorMessage && (hasBlurred || touched || !String(value));

		const handlePaste = useCallback(
			(event: Event) => onPaste?.(event),
			[onPaste],
		);

		const renderInputField = () => {
			const commonProps = {
				value,
				placeholder,
				onInput: handleChange,
				onPaste: handlePaste,
				onBlur: handleBlur,
				disabled,
				...props,
			};

			const inputClassName = cx(
				'p4 h4 lighter lh6 border-radius2 width100 border1 border-grey2 bg-white dark1',
				styles.inputField,
				{
					[styles.textareaField]: multiline,
					[styles.error]: errorMessage,
					[styles.suffixPadding]: !!suffix,
					[styles.prefixPadding]: !!prefix,
				},
				className,
			);

			if (multiline) {
				return (
					<textarea
						ref={ref as Ref<HTMLTextAreaElement>}
						className={inputClassName}
						{...commonProps}
					/>
				);
			}

			return (
				<input
					ref={ref as Ref<HTMLInputElement>}
					type={type}
					className={inputClassName}
					{...commonProps}
				/>
			);
		};

		return (
			<div className={wrapperClassName}>
				{label && (
					<div className={cx('my2 lighter h6 lh6 dark3', labelClassName)}>
						{label}
						{required && <span className={styles.required}>{` *`}</span>}
					</div>
				)}
				<div className="relative">
					{prefix && (
						<div
							className={cx(
								'absolute d-flex justify-center items-center flex-column left0 bottom0',
								styles.contextContainer,
								{ [styles.disabledContext]: disabled },
							)}
						>
							{prefix}
						</div>
					)}
					{renderInputField()}
					{suffix && (
						<div
							className={cx(
								'absolute d-flex justify-center items-center flex-column right0 bottom0',
								styles.contextContainer,
								{ [styles.disabledContext]: disabled },
							)}
						>
							{suffix}
						</div>
					)}
				</div>
				<div
					className={cx(
						'h6 mt1 overflow-hidden d-flex justify-start items-start',
						styles.errorMessage,
						{
							[styles.show]: shouldShowError,
						},
					)}
					id="input-error-text"
				>
					{errorMessage && (
						<Image src={InfoRed} alt={`${label} error`} className="mr1" />
					)}
					{errorMessage}
				</div>
			</div>
		);
	},
);

export default InputBase;
